import PubSub from "./pubsub.js";
import { debounce } from "./utils.js";

// AUGMENTED SEARCH TRIGGER CLICK
if (!customElements.get("augsearch-trigger")) {
  class AugSearchTrigger extends HTMLElement {
    constructor() {
      super();
      this.pubsub = PubSub;
      this.btn = this.querySelector("#aug-search__trigger");
    }

    handleClick = () => {
      this.pubsub.pub("augSearch:triggerClick");
    };

    connectedCallback() {
      this.btn.addEventListener("click", this.handleClick.bind(this));
    }
    disconnectedCallback() {}
  }
  customElements.define("augsearch-trigger", AugSearchTrigger);
}

// AUGMENTED SEARCH PANEL
if (!customElements.get("augsearch-panel")) {
  class AugSearchPanel extends HTMLElement {
    constructor() {
      super();
      this.pubsub = PubSub;
      this.search = this.querySelector("#aug-search");
      this.searchBtn = this.querySelector("#aug-search__button");
      this.results = this.querySelector("#aug-search__results");
      this.panel = this.querySelector("#aug-search__panel");
      this.close = this.querySelector("#aug-search__close-panel");
      this.allResultsForm = this.querySelector("#aug-search__all-results-form");
    }

    // OPEN PANEL & FOCUS INPUT & PREVENT SCROLL
    handleSub = (e) => {
      this.results.innerHTML = "";
      this.hideResults();
      this.panel.classList.remove("hidden");
      this.search.focus();
      document.body.classList.add("overflow-hidden");
    };

    // CLOSE PANEL & CLEAR INPUT & ENABLE SCROLL
    handleClosePanel = (e) => {
      this.panel.classList.add("hidden");
      this.search.value = "";
      document.body.classList.remove("overflow-hidden");
      if (!this.allResultsForm.classList.contains("hidden")) {
        this.allResultsForm.classList.add("hidden");
      }
    };

    // SHOW RESULTS
    showResults = () => {
      this.results.classList.remove("hidden");
    };

    hideResults = () => {
      this.results.classList.add("hidden");
    };

    // RENDER RESULTS
    renderAugResults = async (content, domNode) => {
      const resultsMarkup = new DOMParser()
        .parseFromString(content, "text/html")
        .querySelector("#aug-search__results-inner").innerHTML;
      domNode.innerHTML = resultsMarkup;
    };

    // FETCH RESULTS
    fetchAugResults = async (searchTerm) => {
      await fetch(
        `/search/suggest?q=${searchTerm}&resources[type]=product,article,page,collection&resources[options][fields]=title,product_type,variants.title,body,tag&resources[limit]=20&section_id=aug-search-results`
      )
        .then((response) => {
          if (!response.ok) {
            var error = new Error(response.status);
            this.hideResults();
            if (!this.allResultsForm.classList.contains("hidden")) {
              this.allResultsForm.classList.add("hidden");
            }
            throw error;
          }

          return response.text();
        })
        .then((text) => {
          this.renderAugResults(text, this.results);
          this.showResults();

          const resultsMarkup = new DOMParser()
            .parseFromString(text, "text/html")
            .querySelector("#aug-search__product-results-size").value;

          // console.log("resultsMarkup: ", resultsMarkup);

          if (parseInt(resultsMarkup) > 0) {
            this.allResultsForm.classList.remove("hidden");
          } else {
            if (!this.allResultsForm.classList.contains("hidden")) {
              this.allResultsForm.classList.add("hidden");
            }
          }
        })
        .catch((error) => {
          this.hideResults();
          throw error;
        });
    };

    // HANDLE INPUT
    handleInput = async (e) => {
      // console.log("handleInput: ", e.target.value);
      await this.fetchAugResults(e.target.value);
    };

    connectedCallback() {
      this.searchBtn.addEventListener("click", (e) => {
        e.preventDefault();
      });
      this.pubsub.sub("augSearch:triggerClick", this.handleSub.bind(this));
      this.close.addEventListener("click", this.handleClosePanel.bind(this));
      this.search.addEventListener(
        "input",
        debounce((e) => {
          this.handleInput(e);
        }, 300).bind(this)
      );
    }
    disconnectedCallback() {}
  }
  customElements.define("augsearch-panel", AugSearchPanel);
}

// AUGMENTED SEARCH TABS
if (!customElements.get("augsearch-tabs")) {
  class AugSearchTabs extends HTMLElement {
    constructor() {
      super();
      this.pubsub = PubSub;
      this.tabs = this.querySelectorAll(".aug-search__tab");
      this.panels = this.querySelectorAll(".aug-search__panel");
    }

    handleTabClick = (e) => {
      e.preventDefault();

      if (e.target.classList.contains("active")) {
        return;
      } else {
        const target = e.target.dataset.target;

        this.tabs.forEach((tab) => {
          tab.classList.remove("active");
        });

        e.target.classList.add("active");

        this.panels.forEach((panel) => {
          if (panel.dataset.panel === target) {
            panel.classList.add("active");
          } else {
            panel.classList.remove("active");
          }
        });
      }
    };

    connectedCallback() {
      this.tabs.forEach((tab) => {
        tab.addEventListener("click", this.handleTabClick.bind(this));
      });
    }
    disconnectedCallback() {}
  }
  customElements.define("augsearch-tabs", AugSearchTabs);
}

// AUG SEARCH SLIDER
if (!customElements.get("augsearch-slider")) {
  class AugSearchResults extends HTMLElement {
    constructor() {
      super();

      this.carousel = this.querySelector(".aug-search-carousel");
    }

    connectedCallback() {
      const slider = new BlazeSlider(this.carousel, {
        all: {
          slidesToShow: 1,
          slidesToScroll: 1,

          // loop
          loop: false,

          // autoplay
          enableAutoplay: false,
          stopAutoplayOnInteraction: true,
          autoplayInterval: 3000,
          autoplayDirection: "to left",

          // pagination
          enablePagination: true,

          // transition
          transitionDuration: 500,
          transitionTimingFunction: "ease",
        },
        "(min-width: 0px)": {
          slidesToShow: 1,
          slidesToScroll: 1,
          slideGap: "16px",
        },
        "(min-width: 768px)": {
          slidesToShow: 2,
          slidesToScroll: 2,
          slideGap: "24px",
        },
        "(min-width: 1024px)": {
          slidesToShow: 3,
          slidesToScroll: 3,
          slideGap: "24px",
        },
        "(min-width: 1280px)": {
          slidesToShow: 4,
          slidesToScroll: 4,
          slideGap: "24px",
        },
        "(min-width: 1440px)": {
          slidesToShow: 4,
          slidesToScroll: 4,
          slideGap: "24px",
        },
      });

      // console.log("slider: ", slider);
    }
    disconnectedCallback() {}
  }
  customElements.define("augsearch-slider", AugSearchResults);
}
